import React, { useState, createContext, useContext, useEffect } from "react";
import { useApiService } from "../shared/apiservice";
import { Pallet } from "../shared/types";

const palletContext = createContext<{
    pallet: Pallet | null;
    errors: string[];
    loading: boolean;
    fetchPallet: () => void;
}>({
    pallet: null,
    errors: [],
    loading: false,
    fetchPallet: () => { },
});

function parsePalletItem(d: any) {
    return {
        ...d,
        expires_at: d.expires_at ? new Date(d.expires_at) : null,
    };
}

function parsePallet(d: any): Pallet {
    return {
        id: d.id,
        identifier: d.identifier,
        items: (d.items ?? []).map(parsePalletItem),
    };
}

function useProviderPallet(palletId: number | null | undefined) {
    const [pallet, setPallet] = useState<Pallet | null>(null);
    const [errors, setErrors] = useState<string[]>([]);
    const [loading, setLoading] = useState(false);
    const apiService = useApiService();

    useEffect(() => {
        if (palletId) fetchPallet();
        else {
            setPallet(null);
            setErrors([]);
        }
    }, [palletId]);

    async function fetchPallet() {
        if (!palletId) return;
        setLoading(true);
        setErrors([]);

        try {
            const response = await apiService.get(`/pallets/${palletId}`);
            if (!response.ok) {
                setErrors(["Loading pallet failed. Please try again."]);
                return;
            }
            const json = await response.json();
            setPallet(parsePallet(json.data ?? json));
        } catch (error) {
            console.error(error);
            setErrors(["Loading pallet failed. Please try again."]);
        } finally {
            setLoading(false);
        }
    }

    return { pallet, errors, loading, fetchPallet };
}

export function ProviderPallet({
    children,
    palletId,
}: {
    children: React.ReactNode;
    palletId: number | null | undefined;
}) {
    const value = useProviderPallet(palletId);
    return <palletContext.Provider value={value}>{children}</palletContext.Provider>;
}

export const usePallet = () => {
    return useContext(palletContext);
};
